import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Save, TrendingUp, Copy } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { formatMoeda } from '@fiodecobre/shared';

const MESES = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

interface VendedorBase { id: number; nome: string; perfil: string }

interface FaixaForm { valor_min: number; valor_max: number | null; percentual: number }
interface MetaForm { vendedor_id: number; vendedor_nome: string; meta_mensal: number }
interface SemanaForm { semana_num: number; dia_inicio: number; dia_fim: number; meta_valor: number; premio_valor: number }

const FAIXAS_DEFAULT: FaixaForm[] = [
  { valor_min: 10000, valor_max: 30999, percentual: 1.5 },
  { valor_min: 31000, valor_max: 34999, percentual: 2 },
  { valor_min: 35000, valor_max: null, percentual: 3 },
];

// Gera semanas Domingo->Sabado pra um mes (1a e ultima podem ser parciais).
// O premio sempre fecha no sabado (DA-146); o domingo abre a semana seguinte,
// nunca vira uma "semana" de 1 dia no meio do mes.
function gerarSemanasPadrao(ano: number, mes: number): SemanaForm[] {
  const ultimoDia = new Date(ano, mes, 0).getDate();
  const semanas: SemanaForm[] = [];
  let semNum = 1;
  let diaInicio = 1;
  while (diaInicio <= ultimoDia) {
    // Anda ate o proximo sabado (ou final do mes). Domingo abre uma semana cheia.
    const dow = new Date(ano, mes - 1, diaInicio).getDay(); // 0=Dom, 6=Sab
    const diasAteSabado = (6 - dow + 7) % 7;
    const diaFim = Math.min(diaInicio + diasAteSabado, ultimoDia);
    semanas.push({ semana_num: semNum, dia_inicio: diaInicio, dia_fim: diaFim, meta_valor: 0, premio_valor: 50 });
    semNum++;
    diaInicio = diaFim + 1;
  }
  return semanas;
}

export function ComissoesConfig() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialog();
  const isAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  // Herda o mes/ano selecionado no painel de Comissoes (via query), caindo pra hoje se ausente.
  const [searchParams] = useSearchParams();
  const hoje = new Date();
  const anoInicial = Number(searchParams.get('ano')) || hoje.getFullYear();
  const mesInicial = Number(searchParams.get('mes')) || hoje.getMonth() + 1;
  const [ano, setAno] = useState(anoInicial);
  const [mes, setMes] = useState(mesInicial);

  const [vendedores, setVendedores] = useState<VendedorBase[]>([]);
  const [metaLoja, setMetaLoja] = useState(0);
  const [bonusMetaLoja, setBonusMetaLoja] = useState(400);
  // Uma tabela de faixas so; o SELETOR de modalidade e que decide como ela e aplicada
  // (grava o `tipo` de cada faixa: 'individual' ou 'loja' — o motor le isso, DA-152).
  // Mes novo nasce Individual, que e o modo historico da loja.
  const [modo, setModo] = useState<'individual' | 'loja'>('individual');
  const [faixas, setFaixas] = useState<FaixaForm[]>(FAIXAS_DEFAULT);
  const [metas, setMetas] = useState<MetaForm[]>([]);
  const [semanas, setSemanas] = useState<SemanaForm[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [duplicando, setDuplicando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  // Toggle global (nao por mes) da geracao automatica do titulo de comissao MENSAL (DA-145).
  const [geracaoAuto, setGeracaoAuto] = useState(true);
  // Toggle global do premio SEMANAL — default DESLIGADO (premio pago em dinheiro no sabado, DA-193).
  const [premioSemanalAuto, setPremioSemanalAuto] = useState(false);
  // Faixa sem teto cujo campo "ate" foi aberto pelo botao "por teto".
  const [tetoEditando, setTetoEditando] = useState<number | null>(null);
  const [aba, setAba] = useState<'resumo' | 'geral' | 'vendedoras'>('resumo');

  if (!isAdmin) {
    return <div style={{ padding: 24 }}>Acesso negado</div>;
  }

  const anosOpts: number[] = [];
  for (let y = hoje.getFullYear() - 1; y <= hoje.getFullYear() + 1; y++) anosOpts.push(y);

  async function carregar() {
    setCarregando(true);
    try {
      const v = await api.get<VendedorBase[]>('/api/comissoes/vendedores');
      setVendedores(v);

      try {
        const cfg = await api.get<any>(`/api/comissoes/config/${ano}/${mes}`);
        setMetaLoja(Number(cfg.meta_loja || 0));
        setBonusMetaLoja(Number(cfg.bonus_meta_loja || 400));
        const mapFaixa = (f: any): FaixaForm => ({
          valor_min: Number(f.valor_min),
          valor_max: f.valor_max != null ? Number(f.valor_max) : null,
          percentual: Number(f.percentual),
        });
        const faixasCfg = (cfg.faixas || []) as any[];
        // Modalidade do mes salvo: qualquer faixa 'loja' => Pela Loja (mesma regra do motor).
        setModo(faixasCfg.some((f) => f.tipo === 'loja') ? 'loja' : 'individual');
        setFaixas(faixasCfg.map(mapFaixa));
        setMetas((cfg.metas_vendedor || []).map((m: any) => ({
          vendedor_id: Number(m.vendedor_id),
          vendedor_nome: m.vendedor_nome,
          meta_mensal: Number(m.meta_mensal || 0),
        })));
        setSemanas((cfg.semanas || []).map((s: any) => ({
          semana_num: Number(s.semana_num),
          dia_inicio: Number(s.dia_inicio),
          dia_fim: Number(s.dia_fim),
          meta_valor: Number(s.meta_valor || 0),
          premio_valor: Number(s.premio_valor || 50),
        })));
      } catch (err: any) {
        if (err instanceof ApiError && err.status === 404) {
          // Sem config — defaults.
          setMetaLoja(100000);
          setBonusMetaLoja(400);
          setModo('individual');
          setFaixas(FAIXAS_DEFAULT);
          // Metas: vendedoras (perfil vendedor) com meta 0
          setMetas(v.filter((u) => u.perfil === 'vendedor').map((u) => ({
            vendedor_id: u.id, vendedor_nome: u.nome, meta_mensal: 0,
          })));
          setSemanas(gerarSemanasPadrao(ano, mes));
        } else throw err;
      }
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { void carregar(); }, [ano, mes]);

  // Estado global da geracao automatica (uma vez).
  useEffect(() => {
    api.get<{ valor: string | null }>('/api/app-settings/comissao_geracao_automatica')
      .then((r) => setGeracaoAuto(r.valor == null ? true : r.valor === 'true'))
      .catch(() => setGeracaoAuto(true));
    api.get<{ valor: string | null }>('/api/app-settings/comissao_premio_semanal_automatico')
      .then((r) => setPremioSemanalAuto(r.valor == null ? false : r.valor === 'true'))
      .catch(() => setPremioSemanalAuto(false));
  }, []);

  async function alternarGeracaoAuto(ligado: boolean) {
    setGeracaoAuto(ligado); // otimista
    try {
      await api.put('/api/app-settings/comissao_geracao_automatica', { valor: ligado ? 'true' : 'false' });
      toast.sucesso(ligado ? 'Geracao automatica ligada' : 'Geracao automatica desligada');
    } catch (e: any) {
      setGeracaoAuto(!ligado); // reverte
      toast.erro(e.message);
    }
  }

  async function alternarPremioSemanal(ligado: boolean) {
    setPremioSemanalAuto(ligado); // otimista
    try {
      await api.put('/api/app-settings/comissao_premio_semanal_automatico', { valor: ligado ? 'true' : 'false' });
      toast.sucesso(ligado ? 'Premio semanal vira titulo automaticamente' : 'Premio semanal pago por fora (sem titulo)');
    } catch (e: any) {
      setPremioSemanalAuto(!ligado); // reverte
      toast.erro(e.message);
    }
  }

  function adicionarFaixa() {
    setFaixas([...faixas, { valor_min: 0, valor_max: null, percentual: 0 }]);
  }
  function removerFaixa(i: number) {
    setFaixas(faixas.filter((_, idx) => idx !== i));
  }
  function atualizarFaixa(i: number, campo: keyof FaixaForm, v: any) {
    setFaixas(faixas.map((f, idx) => idx === i ? { ...f, [campo]: v } : f));
  }

  function adicionarMeta(vid: number) {
    const v = vendedores.find((u) => u.id === vid);
    if (!v) return;
    if (metas.some((m) => m.vendedor_id === vid)) return;
    setMetas([...metas, { vendedor_id: vid, vendedor_nome: v.nome, meta_mensal: 0 }]);
  }
  function removerMeta(vid: number) {
    setMetas(metas.filter((m) => m.vendedor_id !== vid));
  }
  function atualizarMeta(vid: number, valor: number) {
    setMetas(metas.map((m) => m.vendedor_id === vid ? { ...m, meta_mensal: valor } : m));
  }

  function regenerarSemanas() {
    setSemanas(gerarSemanasPadrao(ano, mes));
  }
  function atualizarSemana(i: number, campo: keyof SemanaForm, v: any) {
    setSemanas(semanas.map((s, idx) => idx === i ? { ...s, [campo]: v } : s));
  }

  async function salvar() {
    setSalvando(true);
    try {
      await api.post('/api/comissoes/config', {
        mes, ano,
        meta_loja: metaLoja,
        bonus_meta_loja: bonusMetaLoja,
        // A modalidade escolhida vai no `tipo` de cada faixa — e o que o motor le pra decidir
        // se o % incide sobre o volume de cada vendedora ou sobre o faturamento da loja.
        faixas: faixas.map((f) => ({ ...f, tipo: modo })),
        metas_vendedor: metas.map((m) => ({ vendedor_id: m.vendedor_id, meta_mensal: m.meta_mensal })),
        semanas,
      });
      toast.sucesso('Configuracao salva');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  // Copia a config que esta na TELA (inclusive edicoes ainda nao salvas) pro mes seguinte.
  // As semanas sao regeradas pro calendario do mes destino (dias corretos), mas os valores de
  // meta/premio de cada semana sao transportados por posicao. Avisa antes de sobrescrever.
  async function duplicarProximoMes() {
    const proxMes = mes === 12 ? 1 : mes + 1;
    const proxAno = mes === 12 ? ano + 1 : ano;
    const rotulo = `${MESES[proxMes - 1]} ${proxAno}`;

    // Ja existe config no mes destino? Confirma sobrescrita.
    try {
      await api.get<any>(`/api/comissoes/config/${proxAno}/${proxMes}`);
      const ok = await dialog.confirmar(
        `${rotulo} ja tem comissoes configuradas. Sobrescrever com a config de ${MESES[mes - 1]} ${ano}?`,
        { perigoso: true, textoConfirmar: 'Sobrescrever' },
      );
      if (!ok) return;
    } catch (err: any) {
      // 404 = destino vazio, segue direto. Outro erro = aborta.
      if (!(err instanceof ApiError && err.status === 404)) {
        toast.erro(err.message);
        return;
      }
    }

    // Semanas do mes destino (dias corretos) herdando meta/premio por posicao.
    const base = gerarSemanasPadrao(proxAno, proxMes);
    const semanasDestino = base.map((s, i) => semanas[i]
      ? { ...s, meta_valor: semanas[i].meta_valor, premio_valor: semanas[i].premio_valor }
      : s);

    setDuplicando(true);
    try {
      await api.post('/api/comissoes/config', {
        mes: proxMes, ano: proxAno,
        meta_loja: metaLoja,
        bonus_meta_loja: bonusMetaLoja,
        faixas: faixas.map((f) => ({ ...f, tipo: modo })),
        metas_vendedor: metas.map((m) => ({ vendedor_id: m.vendedor_id, meta_mensal: m.meta_mensal })),
        semanas: semanasDestino,
      });
      toast.sucesso(`Comissoes duplicadas para ${rotulo}`);
      // Pula pro mes destino pra revisar (recarrega do banco, ja salvo).
      setAno(proxAno);
      setMes(proxMes);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setDuplicando(false);
    }
  }

  const naoSelecionados = vendedores.filter((u) => !metas.some((m) => m.vendedor_id === u.id));

  // Totalizadores das metas semanais (rodape da tabela) — pra equalizar a soma das semanas
  // com a meta mensal de cada vendedora.
  const somaMetasSemanais = semanas.reduce((a, s) => a + Number(s.meta_valor || 0), 0);
  // Meta mensal de referencia: so compara quando todas as vendedoras tem a MESMA meta (caso comum).
  // Metas diferentes => comparacao ambigua, omite a linha.
  const metasMensais = metas.map((m) => Number(m.meta_mensal || 0));
  const metaMensalReferencia = metasMensais.length > 0 && metasMensais.every((v) => v === metasMensais[0])
    ? metasMensais[0]
    : null;
  const metasBatem = metaMensalReferencia != null && Math.abs(somaMetasSemanais - metaMensalReferencia) < 0.01;

  // Resumo em portugues montado do que esta na TELA (inclusive edicao nao salva): quem volta
  // aqui depois de meses le a regra do mes sem decifrar cada campo.
  const faixasOrdenadas = [...faixas].sort((a, b) => a.valor_min - b.valor_min);
  const pcts = faixasOrdenadas.map((f) => f.percentual).filter((p) => p > 0);
  const fmtPct = (p: number) => `${String(p).replace('.', ',')}%`;
  const metasSemanaisValores = semanas.map((s) => Number(s.meta_valor || 0));
  const premiosSemanaisValores = semanas.map((s) => Number(s.premio_valor || 0));
  const metaSemanalUnica = metasSemanaisValores.length > 0 && metasSemanaisValores.every((v) => v === metasSemanaisValores[0])
    ? metasSemanaisValores[0] : null;
  const premioSemanalUnico = premiosSemanaisValores.length > 0 && premiosSemanaisValores.every((v) => v === premiosSemanaisValores[0])
    ? premiosSemanaisValores[0] : null;

  const cartao = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' } as const;
  const explica = { fontSize: 16, color: 'var(--text-muted)', margin: '2px 0 10px' } as const;

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div className="modal-conteudo" style={{ maxWidth: 1700, width: '96%' }}>
        <div className="modal-header">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={18} />
              Configurar Comissoes
            </span>
            {/* Periodo no cabecalho: a linha propria custava 60px e a tela tem que caber sem rolar. */}
            <select value={mes} onChange={(e) => setMes(Number(e.target.value))} style={{ fontSize: 15 }}>
              {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={ano} onChange={(e) => setAno(Number(e.target.value))} style={{ fontSize: 15 }}>
              {anosOpts.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              type="button"
              className="btn-primario"
              disabled={salvando || duplicando || carregando}
              onClick={() => void duplicarProximoMes()}
              style={{ fontSize: 14, padding: '4px 10px' }}
            >
              <Copy size={13} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
              {duplicando ? 'Duplicando...' : `Duplicar p/ ${MESES[mes === 12 ? 0 : mes]}`}
            </button>
          </span>
          <button type="button" className="btn-fechar-modal" onClick={() => navigate('/comissoes')} aria-label="Fechar">×</button>
        </div>

        <div className="modal-body" style={{ fontSize: 17, paddingTop: 12, paddingBottom: 12 }}>
          {/* Tres abas pra tela caber sem rolar (pedido do Ruan, 26/10/2026). O Salvar grava as
              tres juntas — trocar de aba nao perde o que foi mexido. */}
          <div className="abas" role="tablist" style={{ marginBottom: 10 }}>
            {([
              { valor: 'resumo' as const, rotulo: 'Resumo' },
              { valor: 'geral' as const, rotulo: 'Geral' },
              { valor: 'vendedoras' as const, rotulo: 'Vendedoras' },
            ]).map((a) => (
              <button
                key={a.valor}
                type="button"
                role="tab"
                aria-selected={aba === a.valor}
                className={`aba-btn ${aba === a.valor ? 'aba-btn-ativo' : ''}`}
                onClick={() => setAba(a.valor)}
                style={{ fontSize: 16 }}
              >
                {a.rotulo}
              </button>
            ))}
          </div>

      {carregando ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div> : (
        // Duas colunas fixas, cada cartao com lugar marcado (gridColumn/gridRow) na sua aba.
        // alignItems start: sem isso os cartoes da mesma linha esticam ate a altura do mais alto.
        // Na aba Vendedoras a lista e estreita e as semanas tem 4 colunas: 2/3 da largura pra elas.
        <div style={{ display: 'grid', gridTemplateColumns: aba === 'vendedoras' ? 'minmax(0, 2fr) minmax(0, 3fr)' : 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12, alignItems: 'start' }}>

          {/* Resumo do mes em portugues (o Ruan volta aqui de meses em meses e a tela nao se explicava) */}
          {aba === 'resumo' && (
          <div style={{ ...cartao, gridColumn: '1 / -1', background: 'var(--panel-bg)' }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Como fica {MESES[mes - 1]}</h3>
            <ul style={{ margin: 0, paddingLeft: 22, lineHeight: 1.6 }}>
              <li>
                <strong>Quem participa:</strong>{' '}
                {metas.length === 0 ? 'ninguem — adicione as vendedoras na aba Vendedoras.'
                  : metaMensalReferencia != null
                    ? <>{metas.map((m) => m.vendedor_nome).join(', ')}, meta de <strong>{formatMoeda(metaMensalReferencia)}</strong> cada.</>
                    : <>{metas.map((m) => `${m.vendedor_nome} (${formatMoeda(m.meta_mensal)})`).join(', ')}.</>}
              </li>
              <li>
                <strong>Comissao:</strong>{' '}
                {pcts.length === 0 ? 'nenhuma faixa — comissao 0% pra todas.'
                  : modo === 'loja'
                    ? <>de {fmtPct(Math.min(...pcts))} a {fmtPct(Math.max(...pcts))} sobre o <strong>faturamento da loja</strong>, dividido igual entre as {metas.length}.</>
                    : <>de {fmtPct(Math.min(...pcts))} a {fmtPct(Math.max(...pcts))} sobre o que <strong>cada vendedora recebeu</strong> no mes, conforme a faixa{faixasOrdenadas[0] && faixasOrdenadas[0].valor_min > 0 ? <> (abaixo de {formatMoeda(faixasOrdenadas[0].valor_min)}, nada)</> : null}.</>}
              </li>
              <li>
                <strong>Premio semanal:</strong>{' '}
                {semanas.length === 0 ? 'nenhuma semana cadastrada.'
                  : metaSemanalUnica != null && premioSemanalUnico != null
                    ? <><strong>{formatMoeda(premioSemanalUnico)}</strong> por semana em que a vendedora receber <strong>{formatMoeda(metaSemanalUnica)}</strong>.</>
                    : <>o premio de cada semana em que a vendedora receber a meta daquela semana (aba Vendedoras).</>}
              </li>
              <li>
                <strong>Bonus da loja:</strong>{' '}
                <strong>{formatMoeda(bonusMetaLoja)}</strong> pra cada vendedora se a loja atingir <strong>{formatMoeda(metaLoja)}</strong> no mes.
              </li>
            </ul>
          </div>
          )}

          {/* Vendedoras: o que muda de um mes pro outro (contratacao, saida). */}
          {aba === 'vendedoras' && (
          <div style={{ ...cartao, gridColumn: 1, gridRow: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Vendedoras</h3>
              {naoSelecionados.length > 0 && (
                <select onChange={(e) => { if (e.target.value) adicionarMeta(Number(e.target.value)); e.currentTarget.value = ''; }} defaultValue="">
                  <option value="">+ Adicionar...</option>
                  {naoSelecionados.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
                </select>
              )}
            </div>
            <p style={explica}>So aparece na tela de Comissoes quem estiver nesta lista.</p>
            {metas.length === 0 ? (
              <span style={{ color: 'var(--text-muted)', fontSize: 17 }}>Nenhuma vendedora adicionada</span>
            ) : (
              <table style={{ width: '100%', fontSize: 17 }}>
                <thead><tr><th style={{ textAlign: 'left' }}>Vendedora</th><th style={{ textAlign: 'right' }}>Meta do mes</th><th style={{ width: 28 }}></th></tr></thead>
                <tbody>
                  {metas.map((m) => (
                    <tr key={m.vendedor_id}>
                      <td>{m.vendedor_nome}</td>
                      <td><MoneyInput valor={m.meta_mensal} onChangeValor={(v) => atualizarMeta(m.vendedor_id, v)} style={{ width: '100%', textAlign: 'right' }} /></td>
                      <td><button type="button" className="btn-icon" onClick={() => removerMeta(m.vendedor_id)}><Trash2 size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* Meta da Loja — aba Geral, coluna da direita em cima. */}
          {aba === 'geral' && (
          <div style={{ ...cartao, gridColumn: 2, gridRow: 1 }}>
            <h3 style={{ margin: 0 }}>Meta da loja</h3>
            <p style={explica}>Se a loja inteira atingir a meta, cada vendedora da lista ganha o bonus.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 16, color: 'var(--text-muted)' }}>Meta da loja no mes</label>
                <MoneyInput valor={metaLoja} onChangeValor={setMetaLoja} style={{ width: '100%' }} />
              </div>
              <div>
                <label style={{ fontSize: 16, color: 'var(--text-muted)' }}>Bonus pra cada vendedora</label>
                <MoneyInput valor={bonusMetaLoja} onChangeValor={setBonusMetaLoja} style={{ width: '100%' }} />
              </div>
            </div>
          </div>
          )}

          {/* Faixas de comissao (uma tabela; o seletor de modalidade decide como ela e aplicada).
              Aba Geral, coluna da esquerda ocupando as duas linhas. */}
          {aba === 'geral' && (
          <div style={{ ...cartao, gridColumn: 1, gridRow: '1 / span 2' }}>
            {/* Seletor de modalidade do mes (DA-438). Antes o modo era implicito na presenca
                de faixa 'loja' e a tela so gravava 'loja' — nao havia caminho de volta.
                Mora na linha do titulo pra aba Geral caber sem rolar. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, flex: 1 }}>Faixas de comissao</h3>
              <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>Modalidade:</span>
              <div role="radiogroup" aria-label="Modalidade de comissao" style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--primary)' }}>
                {([
                  { valor: 'individual' as const, rotulo: 'Por vendedora' },
                  { valor: 'loja' as const, rotulo: 'Pela loja (rateio igual)' },
                ]).map((o) => (
                  <button
                    key={o.valor}
                    type="button"
                    role="radio"
                    aria-checked={modo === o.valor}
                    onClick={() => setModo(o.valor)}
                    style={{
                      border: 0, padding: '7px 16px', fontSize: 16, fontWeight: 600, cursor: 'pointer',
                      background: modo === o.valor ? 'var(--primary)' : '#fff',
                      color: modo === o.valor ? '#fff' : 'var(--primary)',
                    }}
                  >
                    {o.rotulo}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 15, color: '#000', backgroundColor: '#fde68a', borderRadius: 6, padding: '6px 10px', marginBottom: 6 }}>
              {modo === 'loja' ? (
                <>
                  <strong>Comissao Pela Loja</strong> (rateio igualitario): o % incide sobre o
                  <strong> faturamento real da loja</strong> e o bolo e dividido em partes iguais entre as {metas.length || 0} vendedora(s) com meta.
                </>
              ) : (
                <>
                  {/* "vendas recebidas", nao "vendeu": crediario so entra quando o cliente quita,
                      no mes do pagamento — e venda devolvida sai. Ver getTotalRecebidoPorVendedor. */}
                  <strong>Comissao por vendedora</strong> (individual): o % da faixa incide sobre as
                  <strong> vendas recebidas de cada vendedora</strong> no mes — cada uma cai na propria faixa.
                </>
              )}
              {' '}Comece a primeira faixa em <strong>R$ 0</strong> para nenhum mes zerar a comissao de todas.
              {faixas.length === 0 && (
                <> <strong>Sem nenhuma faixa a comissao fica em 0% pra todas.</strong></>
              )}
            </div>
            {/* Linha lida como frase: "de X ate Y -> Z%". Os campos tem largura propria; antes De e Ate
                ocupavam a largura toda e o % ficava isolado na outra ponta da tela. */}
            <table style={{ fontSize: 17, borderCollapse: 'separate', borderSpacing: '0 2px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', fontWeight: 600 }} colSpan={2}>Recebido no mes</th>
                  <th></th>
                  <th style={{ textAlign: 'left', fontWeight: 600 }} colSpan={2}>Comissao</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {faixas.map((f, i) => (
                  <tr key={i}>
                    <td style={{ paddingRight: 6, color: 'var(--text-muted)' }}>de</td>
                    <td><MoneyInput valor={f.valor_min} onChangeValor={(v) => atualizarFaixa(i, 'valor_min', v)} style={{ width: 170, textAlign: 'right' }} /></td>
                    <td style={{ padding: '0 6px' }}>
                      {/* Sem teto: o valor gravado e NULL e a tela diz "e acima" — o "0,00" de antes
                          se lia como valor de verdade. Clicar abre o campo pra por um teto. */}
                      {f.valor_max == null && tetoEditando !== i ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, width: 205 }}>
                          <strong>e acima</strong>
                          <button type="button" className="btn-secundario" onClick={() => setTetoEditando(i)} style={{ fontSize: 13, padding: '2px 8px' }}>por teto</button>
                        </span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: 'var(--text-muted)' }}>ate</span>
                          <MoneyInput
                            valor={f.valor_max ?? 0}
                            autoFocus={tetoEditando === i}
                            onChangeValor={(v) => { atualizarFaixa(i, 'valor_max', v > 0 ? v : null); setTetoEditando(null); }}
                            style={{ width: 170, textAlign: 'right' }}
                          />
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0 6px', color: 'var(--text-muted)' }}>→</td>
                    <td>
                      <input type="number" step="0.01" value={f.percentual} onChange={(e) => atualizarFaixa(i, 'percentual', Number(e.target.value))} style={{ width: 80, textAlign: 'right' }} />
                    </td>
                    <td style={{ paddingLeft: 4 }}>%</td>
                    <td style={{ paddingLeft: 10 }}><button type="button" className="btn-icon" onClick={() => removerFaixa(i)}><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* O botao mora ABAIXO da tabela, onde a linha nova aparece. No canto do cabecalho
                ele parecia acao do cartao inteiro e nao se sabia o que ele adicionava. */}
            <div style={{ display: 'flex', marginTop: 10 }}>
              {/* inline-flex + nowrap: sem isso o icone e o texto quebram em duas linhas
                  (o botao e item de flex e encolhe ate o conteudo caber). */}
              <button
                type="button"
                className="btn-primario"
                onClick={adicionarFaixa}
                style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
              >
                <Plus size={14} /> Adicionar faixa
              </button>
            </div>
          </div>
          )}

          {/* Semanas — aba Vendedoras, coluna da direita (a meta semanal vale por vendedora). */}
          {aba === 'vendedoras' && (
          <div style={{ ...cartao, gridColumn: 2, gridRow: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Metas semanais</h3>
              <button type="button" className="btn-secundario" onClick={regenerarSemanas}>Regerar semanas</button>
            </div>
            <table style={{ width: '100%', fontSize: 17 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Semana</th>
                  <th style={{ textAlign: 'left' }}>Dias</th>
                  <th style={{ textAlign: 'right' }}>Meta de cada vendedora</th>
                  <th style={{ textAlign: 'right' }}>Premio</th>
                </tr>
              </thead>
              <tbody>
                {semanas.map((s, i) => (
                  <tr key={s.semana_num}>
                    <td>{s.semana_num}</td>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: 8 }}>Dias {s.dia_inicio}-{s.dia_fim}</td>
                    <td><MoneyInput valor={s.meta_valor} onChangeValor={(v) => atualizarSemana(i, 'meta_valor', v)} style={{ width: '100%', textAlign: 'right' }} /></td>
                    <td><MoneyInput valor={s.premio_valor} onChangeValor={(v) => atualizarSemana(i, 'premio_valor', v)} style={{ width: '100%', textAlign: 'right' }} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td colSpan={2} style={{ textAlign: 'right', paddingTop: 8, paddingRight: 8 }}>Total</td>
                  <td style={{ textAlign: 'right', paddingTop: 8, paddingRight: 12 }}>{formatMoeda(somaMetasSemanais)}</td>
                  <td style={{ textAlign: 'right', paddingTop: 8, paddingRight: 12 }}>{formatMoeda(semanas.reduce((a, s) => a + Number(s.premio_valor || 0), 0))}</td>
                </tr>
              </tfoot>
            </table>
            {/* Frase no lugar do antigo "R$ X — difere R$ Y", que nao dizia o que comparava. */}
            {metaMensalReferencia != null && (
              <div style={{ marginTop: 10, textAlign: 'right' }}>
                <span style={metasBatem
                  ? { backgroundColor: '#fde68a', color: '#000', padding: '2px 10px', borderRadius: 6 }
                  : { color: '#b45309', fontWeight: 600 }}>
                  {metasBatem
                    ? `As semanas somam ${formatMoeda(somaMetasSemanais)} — igual a meta do mes de cada vendedora.`
                    : `As semanas somam ${formatMoeda(somaMetasSemanais)} por vendedora; a meta do mes de cada uma e ${formatMoeda(metaMensalReferencia)}.`}
                </span>
              </div>
            )}
          </div>
          )}

          {/* Automacao dos titulos — global (nao por mes) e quase nunca mexida. Aba Geral, embaixo
              da Meta da loja. DA-145 / DA-193 */}
          {aba === 'geral' && (
          <div style={{ ...cartao, gridColumn: 2, gridRow: 2 }}>
            {/* Textos encurtados pra aba caber sem rolar; o sentido de cada caixa e o mesmo de antes. */}
            <h3 style={{ margin: '0 0 8px' }}>
              Automacao dos titulos{' '}
              <span style={{ fontSize: 15, fontWeight: 400, color: 'var(--text-muted)' }}>— vale pra todos os meses; salva na hora, sem o botao Salvar</span>
            </h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
              <input type="checkbox" checked={geracaoAuto} onChange={(e) => void alternarGeracaoAuto(e.target.checked)} />
              <span style={{ fontWeight: 600 }}>Gerar titulo de comissao automaticamente</span>
            </label>
            <p style={{ fontSize: 15, color: 'var(--text-muted)', margin: '2px 0 10px 24px' }}>
              No 1o dia util, cria em Contas a Pagar a comissao do mes anterior. Desligado: so pelo botao "Gerar Titulos". Regera enquanto pendente; pago trava.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
              <input type="checkbox" checked={premioSemanalAuto} onChange={(e) => void alternarPremioSemanal(e.target.checked)} />
              <span style={{ fontWeight: 600 }}>Gerar titulo do premio semanal automaticamente</span>
            </label>
            <p style={{ fontSize: 15, color: 'var(--text-muted)', margin: '2px 0 0 24px' }}>
              <strong>Desligado (padrao):</strong> premio pago em dinheiro no sabado, fora do sistema. Ligado: cada semana batida vira titulo no sabado as 10h.
            </p>
          </div>
          )}
        </div>
      )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secundario" onClick={() => navigate('/comissoes')} disabled={salvando || duplicando}>Cancelar</button>
          <button type="button" className="btn-primario" disabled={salvando || duplicando || carregando} onClick={() => void salvar()}>
            <Save size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
